/**
 * Public set-password page for an invited INTERNAL/system user.
 *
 * Staff invitations now land here (crm.xopnetworks.com) instead of
 * the customer support portal. It talks to portal-api's public,
 * token-gated invitation endpoints via the dashboard server proxy
 * (/api/auth/accept-invitation*). No session — the token IS the auth.
 * After the password is set the user signs in normally at /login.
 */
import { Box, Button, Card, CardContent, CardHeader, Divider, TextField, Typography } from "@mui/material";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { DesktopDownloads } from "../components/DesktopDownloads.js";
import { ApiError, api } from "../lib/api.js";

interface Preview {
  email: string;
  organization_name: string;
  role: string;
  expires_at: string | null;
}

export function AcceptInvitationPage(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = (params.get("token") ?? "").trim();

  const [state, setState] = useState<
    "loading" | "ready" | "invalid" | "done"
  >("loading");
  const [preview, setPreview] = useState<Preview | null>(null);
  const [loadError, setLoadError] = useState<string>("");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setState("invalid");
      setLoadError("This invitation link is missing its token.");
      return;
    }
    let cancelled = false;
    api<{ data: Preview }>(
      `/api/auth/accept-invitation/preview?token=${encodeURIComponent(token)}`,
      { anonymous: true },
    )
      .then((r) => {
        if (cancelled) return;
        setPreview(r.data);
        setState("ready");
      })
      .catch((e) => {
        if (cancelled) return;
        setState("invalid");
        setLoadError(
          e instanceof ApiError
            ? e.message
            : "This invitation is invalid or has expired.",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    if (password.length < 12) {
      setFormError("Use at least 12 characters.");
      return;
    }
    if (password !== confirm) {
      setFormError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api("/api/auth/accept-invitation", {
        method: "POST",
        anonymous: true,
        body: { token, password, password_confirmation: confirm },
      });
      setState("done");
    } catch (err) {
      setFormError(
        err instanceof ApiError
          ? err.message
          : "Could not set your password. The link may have expired.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 2,
      }}
    >
      <Card sx={{ width: "100%", maxWidth: "28rem" }}>
        <CardHeader title="XOP Command Center — set your password" />
        <CardContent>
          {state === "loading" && (
            <Typography sx={{ fontSize: 14, color: "text.disabled" }}>
              Checking your invitation…
            </Typography>
          )}

          {state === "invalid" && (
            <Box sx={{ display: "grid", gap: 1.5 }}>
              <Typography sx={{ fontSize: 14, color: "error.main" }}>
                {loadError}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                Ask an administrator to resend your invite.
              </Typography>
              <Box>
                <Button variant="contained" disableElevation onClick={() => navigate("/login")}>
                  Go to sign in
                </Button>
              </Box>
            </Box>
          )}

          {state === "done" && (
            <Box sx={{ display: "grid", gap: 2 }}>
              <Typography sx={{ fontSize: 14, color: "success.main" }}>
                Your password is set. You're ready to go.
              </Typography>
              <Box>
                <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>
                  Install the desktop app
                </Typography>
                <DesktopDownloads />
              </Box>
              <Divider />
              <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
                Prefer the browser for now? You can also sign in on the web.
              </Typography>
              <Box>
                <Button variant="outlined" disableElevation onClick={() => navigate("/login")}>
                  Sign in on the web
                </Button>
              </Box>
            </Box>
          )}

          {state === "ready" && preview && (
            <Box component="form" onSubmit={onSubmit} sx={{ display: "grid", gap: 2 }}>
              <Box sx={{ fontSize: 14, color: "text.secondary" }}>
                <Typography component="p" sx={{ fontSize: 14 }}>
                  Welcome — you've been invited to{" "}
                  <strong>{preview.organization_name}</strong> as{" "}
                  <strong>{preview.role}</strong>.
                </Typography>
                <Typography component="p" sx={{ mt: 0.5, fontSize: 12, color: "text.disabled" }}>
                  Account: {preview.email}
                </Typography>
              </Box>
              <TextField
                label="New password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                size="small"
                fullWidth
              />
              <TextField
                label="Confirm password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                size="small"
                fullWidth
              />
              {formError && (
                <Typography sx={{ fontSize: 12, color: "error.main" }}>
                  {formError}
                </Typography>
              )}
              <Box>
                <Button type="submit" variant="contained" disableElevation disabled={submitting}>
                  {submitting ? "Setting…" : "Set password & continue"}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
